import { useEffect, useState } from "react";
import { fetchDealOfTheDay, redeemCoupon, logClick } from "@/lib/api";
import { getConfig, wrapAffiliate } from "@/lib/affiliate";
import StoreLogo from "@/components/StoreLogo";
import { Copy, Check, ExternalLink, Star } from "lucide-react";
import { toast } from "sonner";
import { addToCopyHistory } from "@/lib/copyHistory";

export default function DealOfTheDay() {
  const [deal, setDeal] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchDealOfTheDay()
      .then((d) => mounted && setDeal(d))
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!deal) return null;

  const handleCopy = async () => {
    setRevealed(true);
    if (deal.code) {
      try {
        await navigator.clipboard.writeText(deal.code);
        setCopied(true);
        addToCopyHistory(deal);
        toast.success(`Code ${deal.code} copied`, { duration: 2000 });
        setTimeout(() => setCopied(false), 2200);
      } catch {
        toast.info(`Code: ${deal.code}`);
      }
    }
    redeemCoupon(deal.id).catch(() => {});
  };

  const handleGrab = async () => {
    let target = deal.store_url;
    try {
      const cfg = await getConfig();
      target = wrapAffiliate(deal.store_url, cfg);
    } catch {}
    logClick(deal.id);
    if (target) window.open(target, "_blank", "noopener");
    redeemCoupon(deal.id).catch(() => {});
  };

  return (
    <section
      data-testid="deal-of-the-day"
      className="mb-10 relative overflow-hidden rounded-2xl border-2 border-[#2d2a32] hard-shadow-sm"
      style={{
        background:
          "linear-gradient(135deg, #ff6b6b 0%, #f2a93b 50%, #f7c873 100%)",
      }}
    >
      {/* Decorative stripes */}
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
           style={{
             background:
               "repeating-linear-gradient(45deg, #2d2a32 0px, #2d2a32 2px, transparent 2px, transparent 14px)",
           }}
      />

      <div className="relative p-6 md:p-8 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 items-center">
        {/* Left: logo + badge */}
        <div className="flex flex-col items-center gap-3">
          <div className="border-2 border-[#2d2a32] rounded-2xl p-3 bg-white hard-shadow-sm shrink-0">
            <StoreLogo
              store={deal.store}
              storeUrl={deal.store_url}
              size={68}
              testid="dotd-store-logo"
            />
          </div>
          <span
            className={`mono text-[10px] tracking-[0.28em] font-black px-3 py-1 border-2 border-[#2d2a32] -rotate-3 inline-flex items-center gap-1 ${
              deal._dotd_sponsored ? "bg-[#f7c873] text-[#2d2a32]" : "bg-[#2d2a32] text-[#f7c873]"
            }`}
            style={{ borderRadius: 6 }}
            data-testid="dotd-badge"
          >
            <Star className="w-3 h-3 fill-current" />
            {deal._dotd_sponsored ? "SPONSORED · DEAL OF THE DAY" : "DEAL OF THE DAY"}
          </span>
        </div>

        {/* Center: title, description, code */}
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.3em] text-[#2d2a32] font-bold mb-1">
            {deal.category?.toUpperCase()}
          </div>
          <h2
            className="serif text-4xl md:text-5xl leading-[0.98] text-[#2d2a32] font-bold mb-2"
            data-testid="dotd-store"
          >
            {deal.store}
          </h2>
          <div
            className="inline-block px-3 py-1 text-sm font-black tracking-[0.15em] text-[#2d2a32] bg-white border-2 border-[#2d2a32] rounded-md -rotate-1 mb-3"
            data-testid="dotd-discount"
          >
            {deal.discount_label}
          </div>
          <p
            className="text-[15px] md:text-[16px] text-[#2d2a32] leading-snug font-semibold mb-2 line-clamp-2"
            data-testid="dotd-title"
          >
            {deal.title}
          </p>
          {deal.description && (
            <p className="text-sm text-[#2d2a32]/80 leading-snug line-clamp-2 max-w-2xl">
              {deal.description}
            </p>
          )}
        </div>

        {/* Right: CTA */}
        <div className="flex flex-col gap-3 min-w-[180px] w-full md:w-auto">
          {deal.code ? (
            <button
              onClick={handleCopy}
              data-testid="dotd-copy-code"
              className="w-full text-left border-2 border-dashed border-[#2d2a32] bg-white/95 hover:bg-white rounded-lg px-4 py-3"
              aria-label="Copy DOTD code"
            >
              <div className="text-[9px] tracking-[0.25em] text-[#5c5c5c] mb-1">
                {revealed ? "COPIED" : "TAP TO COPY"}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="mono text-lg tracking-widest font-bold text-[#2d2a32]">
                  {revealed ? deal.code : "•••••••"}
                </span>
                {copied ? (
                  <Check className="w-5 h-5 text-[#2d2a32]" />
                ) : (
                  <Copy className="w-5 h-5 text-[#2d2a32]" />
                )}
              </div>
            </button>
          ) : (
            <div className="border-2 border-dashed border-[#2d2a32] bg-white/95 rounded-lg px-4 py-3 text-center">
              <div className="text-[9px] tracking-[0.25em] text-[#5c5c5c] mb-1">NO CODE NEEDED</div>
              <div className="mono text-xs font-bold text-[#2d2a32]">AUTO-APPLIED ✨</div>
            </div>
          )}
          <button
            onClick={handleGrab}
            data-testid="dotd-grab-btn"
            className="w-full h-12 rounded-full bg-[#2d2a32] hover:bg-black text-white text-[11px] tracking-[0.28em] font-black inline-flex items-center justify-center gap-2 shadow-lg"
          >
            GRAB DEAL <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
